import { IConversationBulkAction, IConversationUpdate } from '../models'

import BaseService from './BaseService'
import { NEW_MESSAGE_FLOW_CONVERSATION_ID } from '../util/constants'

declare enum AuditVoteType {
  UP = 'up',
  DOWN = 'down'
}
interface WhatsAppLocationSetupBody {
  companyHQCountry: string
  companyName: string
  companyVertical: string
  companyWebsite: string
  facebookBusinessManagerId: string
  numOfMessages: string
  regionSendingTo: string
  twilioAccountSID: string
  useCase: string
  location: string
}

interface WhatsAppNumberSetupBody {
  locationId: string,
  number: string,
  displayName: string,
  address?: string,
  companyEmail?: string,
  website?: string,
  logo?: string,
}

export interface CheckForSpamBody {
  text: string
  locationId: string
  contactId?: string
}

export interface UpdateConversationBody{
  thumbVote?: AuditVoteType
  attachmentsSyncStatus?:{
    url:string
    isSynced:boolean
  }[]
}

export interface InboundMessageSuggestion{
  thumbVote: AuditVoteType,
  conversationId: string,
  locationId: string
}

class ConversationsService extends BaseService {
  constructor(baseUrl: string) {
    super(baseUrl)
  }

  message = (body) => this.requests.post('/messages', body)
  getMessageByAltId = (altId: string) => this.requests.get(`/messages/search?altId=${altId}`)
  // WhatsApp Location Setup APIs
  getWhatsAppLocationSetupStatus = (locationId: string) =>
    this.requests.get(
      `/providers/whatsapp/location/${locationId}/status`
    )
  postWhatsAppLocationSetupRequest = (body: WhatsAppLocationSetupBody) =>
    this.requests.post(
      '/providers/whatsapp/location/setup',
      body
    )
  getWhatsAppNumbers = (locationId: string) =>
    this.requests.get(
      `/providers/whatsapp/${locationId}/numbers`
    )
  postWhatsAppNumberSetupRequest = (body: WhatsAppNumberSetupBody) =>
    this.requests.post(
      '/providers/whatsapp/number/setup',
      body
    )
  enableWhatsAppNumber = (locationId: string, number:string) =>
    this.requests.put(`/providers/whatsapp/number/state`,{
      state: "enable",
      number,
      locationId
    })

  getById = (conversationId:string) => this.requests.get(`/${conversationId}`)
  getConversationsV2 = (filterString: string) => this.requests.get(`/search?${filterString}`)
  performBulkActions = (data: IConversationBulkAction) => this.requests.post(`/bulk`, data)
  updateConversation = (id: string, data: IConversationUpdate) => this.requests.put(`/${id}`, data)

  updateFilters = async (userId: string, locationId: string, filters: any) => {
    const {
      leftPanel: {
        assignedTo,
        followers,
        lastMessageType,
        lastMessageDirection,
        lastMessageAction,
        sortScoreProfile,
        scoreProfile,
        scoreProfileMin,
        scoreProfileMax,
        sort,
        sortBy,
      },
    } = filters

    const reqBody = {
      conversations: {
        leftPanel: {
          assignedTo,
          followers,
          lastMessageType,
          lastMessageDirection,
          lastMessageAction,
          sortScoreProfile,
          scoreProfile,
          scoreProfileMin,
          scoreProfileMax,
          sort,
          sortBy
        }
      }
    }
    this.requests.put(`/filters?userId=${userId}&locationId=${locationId}`, reqBody)
  }

  fetchFilters = (userId: string, locationId: string) =>
    this.requests.get(`/filters?userId=${userId}&locationId=${locationId}`)

    // email/sms usage stats
  getMessageUsageStats = (locationId: string, type: string) =>
    this.requests.get(`/messages/${locationId}/${type}/usage`)

  updateMessageUsageStats = (locationId: string, type: string, newLimit: string) =>
    this.requests.put(`/messages/${locationId}/${type}/usage?newLimit=${newLimit}`,{})

  getMessages = threadID =>
    this.requests.get(
      `/messages/email/search?threadId=${threadID}&deleted=false`
    )
  getThread = threadID => this.requests.get(`/messages/${threadID}`)
  getThreadEmailMsg = emailMessageId => this.requests.get(`/messages/email/${emailMessageId}`)
  getRateLimit = (locationId) => this.requests.get(`/messages/${locationId}/rate-limit/twilio`, {})

  fetchDomainsByQuery = (params) =>
      this.requests.get(`/providers/mailgun/domains`, {params})
        .then(data => ({data}))
        .catch((error) => error?.response)

  cancelScheduledMessage = (data: {email: boolean, id: string}) => {
    if (data.email) {
      return this.requests.delete(`/messages/email/${data.id}/schedule`)
    }
    return this.requests.delete(`/messages/${data.id}/schedule`)
  }
  getMessagesForConversation = (conversationId: string, params: any) => {
    if (conversationId === NEW_MESSAGE_FLOW_CONVERSATION_ID) {
      return {
        messages: [],
        nextPage: false
      }
    }
    return this.requests.get(`/${conversationId}/messages`, {params})
  }

  checkForSpam = (body: CheckForSpamBody): Promise<{ isSpam: boolean }> => {
    return this.requests.post(
      '/messages/spam/v2',
      body
    )
  }
  deleteConversation = (conversationId: string) => this.requests.delete(`/${conversationId}`)

  updateMessage = (messageId, body: UpdateConversationBody ) =>
  this.requests.put(`/messages/${messageId}`, body)

  updateInboundMessageSuggestion = (suggestionId: string, body: InboundMessageSuggestion ) =>
  this.requests.put(`/inbound/message/suggestion/${suggestionId}`, body)

  recording = (locationId: string, messageId: string, index:number) => {
    return this.requests.get(
      `/messages/${messageId}/locations/${locationId}/recording?index=${index}`,{
        responseType: 'blob'
      }
    )
  }

  requestContactDetails=async (body)=>{
    return this.requests.post(`/providers/live-chat/user/request/contact`,
    body
    )
  }

  endLiveChat=async (body)=>{
    return this.requests.post(`/providers/live-chat/user/close`,
    body
    )
  }
  getMessageById = (messageId: string) => this.requests.get(`/messages/${messageId}`)
  transcription = (locationId: string, messageId: string, index: number) => {
    return this.requests.get(
      `/locations/${locationId}/messages/${messageId}/transcription?index=${index}`
    )

  }

  getDraftMessages = (locationId: string, contactId: string) => {
    return this.requests.get(`/messages/drafts/${locationId}/contacts/${contactId}`)
  }

  saveDraftMessage = (body) => {
    return this.requests.post(`/messages/drafts`, body)
  }

  deleteDraftMessageById = (draftId: string) => {
    return this.requests.delete(`/messages/drafts/${draftId}`)
  }

  deleteDraftMessageByContactChannel = (locationId: string, contactId: string, channel: string, providerId?: string) => {
    if (!contactId ) return
    let url = `/messages/drafts/${locationId}/contacts/${contactId}/${channel}`
    if (providerId) url += `/${providerId}`
    return this.requests.delete(url)
  }

  deleteInternalComment = (commentId: string) => {
    console.log('comment deleting', commentId)
    return this.requests.delete(`/messages/internal-comment/${commentId}`)
  }
}

const instance = new ConversationsService(BaseService.servicesConfig.Conversations)

export default instance
